import { Experiment } from '@ms/yammer-data/dist/state/types';
import { AnalyticsEventName, reportUsage } from '@ms/yammer-data/dist/telemetry';
import { useLoadCurrentUserNetworks } from '@ms/yammer-hooks/dist/currentUserNetworks';
import { useAndReportExperimentTreatment } from '@ms/yammer-hooks/dist/experiments';
import VivaEngageNavigationHeaderMoreActions from '@ms/yammer-web-components/dist/teams/VivaEngageNavigationHeaderMoreActions';
import VivaEngageNavigationHeaderPivot from '@ms/yammer-web-components/dist/teams/VivaEngageNavigationHeaderPivot';
import {
  SidebarPanelButton,
  useIsContentSidebarCurrentlyHiddenByNarrowWidth,
} from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import Block from '@ms/yammer-web-core-ui/dist/components/Block';
import Flex, { FlexItem } from '@ms/yammer-web-core-ui/dist/components/Flex';
import InternalLink from '@ms/yammer-web-core-ui/dist/components/InternalLink';
import { LayoutList, LayoutListItem } from '@ms/yammer-web-core-ui/dist/components/LayoutList';
import { useIsPageLayoutVNext } from '@ms/yammer-web-core-ui/dist/components/PageLayout';
import { getRTL } from '@ms/yammer-web-core-ui/dist/util/styling/rtl';
import { feedPageUrl } from '@ms/yammer-web-support/dist/urls';
import { useUIFeatures } from '@ms/yammer-web-ui-features/dist/features';
import React, { FC, SyntheticEvent } from 'react';
import { useHistory } from 'react-router-dom';

import AppHeaderLogo from '../AppHeaderLogo';
import { usePageContext } from '../PageContext';
import Toast from '../Toast';
import VivaEngageAppInbox from '../VivaEngageAppInbox';
import VivaEngageAppInsights from '../VivaEngageAppInsights';
import VivaEngageAppNotifications from '../VivaEngageAppNotifications';
import VivaEngageAppSearch from '../VivaEngageAppSearch';
import VivaEngagePanelButton from '../VivaEngagePanelButton';
import VivaSuiteNav from '../VivaSuiteNav';

import { getClassNames } from './VivaEngageAppHeader.styles';
import { useGetNavigationHeaderPivotItems } from './hooks';

const toastTargetId = 'vivaEngageAppHeader--toastTarget';

const VivaEngageAppHeader: FC = () => {
  useLoadCurrentUserNetworks();

  const features = useUIFeatures(({ navigation }) => navigation ?? {});

  const isAppRTL = getRTL();
  const className = getClassNames(isAppRTL);
  const pivotItems = useGetNavigationHeaderPivotItems();
  const { location } = useHistory();
  const { shouldDisplayContentPanelButton } = usePageContext();

  const isVivaSuiteNavEnabled = useAndReportExperimentTreatment(Experiment.VivaEngageSuiteNav) === true;

  const isPageLayoutVNext = useIsPageLayoutVNext();
  const shouldDisplayAppLayoutSidebarPanelButton =
    useIsContentSidebarCurrentlyHiddenByNarrowWidth() && isPageLayoutVNext;

  const legacyPageLayoutSidebarPanelButton = shouldDisplayContentPanelButton ? <VivaEngagePanelButton /> : null;
  const vNextPageLayoutSidebarPanelButton = shouldDisplayAppLayoutSidebarPanelButton ? <SidebarPanelButton /> : null;
  const sidebarPanelButton = vNextPageLayoutSidebarPanelButton || legacyPageLayoutSidebarPanelButton;
  const sidebarPanelButtonItem = sidebarPanelButton ? <LayoutListItem>{sidebarPanelButton}</LayoutListItem> : null;

  const searchItem = features.search ? (
    <FlexItem>
      <VivaEngageAppSearch />
    </FlexItem>
  ) : null;

  const notificationsItem = features.notifications ? (
    <FlexItem className={className.notificationsItem}>
      <VivaEngageAppNotifications />
    </FlexItem>
  ) : null;

  const inboxItem = features.inbox ? (
    <FlexItem className={className.inboxItem}>
      <VivaEngageAppInbox />
    </FlexItem>
  ) : null;

  const insightsItem = features.insights ? (
    <FlexItem>
      <VivaEngageAppInsights />
    </FlexItem>
  ) : null;

  const onAppHeaderLogoClick = (event: SyntheticEvent) => {
    if (location.pathname === feedPageUrl) {
      event.preventDefault();
    }
    reportUsage({ eventName: AnalyticsEventName.VivaEngageIconClicked });
  };

  const vivaSuiteNav = isVivaSuiteNavEnabled ? (
    <Flex shrink={0} paddingLeft={4.2} paddingBottom={1} alignItems="center">
      <VivaSuiteNav />
    </Flex>
  ) : null;

  const appHeaderContent = (
    <Flex className={className.contentWrapper}>
      {vivaSuiteNav}
      <Flex shrink={0} paddingLeft={isVivaSuiteNavEnabled ? 1.75 : 4.2} alignItems="center">
        <InternalLink
          onClick={onAppHeaderLogoClick}
          to={feedPageUrl}
          className={className.internalLink}
          unstyled={true}
        >
          <AppHeaderLogo />
        </InternalLink>
      </Flex>
      <VivaEngageNavigationHeaderPivot items={pivotItems} linkSize="normal" linkFormat="links" />
      <Flex justifyContent="flex-end" alignItems="center" grow={1} shrink={0}>
        <LayoutList direction="horizontal" isPresentational={true} preventItemWrap={true} showSeparator={true}>
          {sidebarPanelButtonItem}
          <LayoutListItem fillWidth={true}>
            <Flex columnGap={1.5}>
              {searchItem}
              {notificationsItem}
              {inboxItem}
              {insightsItem}
              <FlexItem className={className.moreActionsItem}>
                <span id={toastTargetId} />
                <VivaEngageNavigationHeaderMoreActions />
              </FlexItem>
            </Flex>
          </LayoutListItem>
        </LayoutList>
        <Toast targetElementId={toastTargetId} />
      </Flex>
    </Flex>
  );

  return (
    <Block className={className.container} paddingTop={1} paddingRight={6} textAlign="right">
      {appHeaderContent}
    </Block>
  );
};

export default VivaEngageAppHeader;
